import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import moment from 'moment';
import { MaterialModule } from 'src/app/material.module';
import { NgxMaterialTimepickerModule, NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { IDateRangePickerConfig } from 'src/app/shared/interfaces/IDate-picker';
const CUSTOM_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY'
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMMM Y',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM Y'
  }
};

@Component({
  selector: 'app-form-date-range-picker',
  standalone: true,
  imports: [
    MaterialModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    NgxMaterialTimepickerModule
  ],
  templateUrl: './form-date-range-picker.component.html',
  styleUrls: ['./form-date-range-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDateRangePickerComponent),
      multi: true
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: DateAdapter, useClass: MomentDateAdapter }
  ]
})
export class FormDateRangePickerComponent implements OnInit, ControlValueAccessor {
  @ViewChild('startTimepicker') startTimepicker: any;
  @ViewChild('endTimepicker') endTimepicker: any;
  @Input() dateRangePickerConfig: IDateRangePickerConfig = {};
  startDate: any;
  endDate: any;
  startTime = '';
  endTime = '';
  tempStartTime = '';
  tempEndTime = '';
  isDisabled = false;
  lightTheme: NgxMaterialTimepickerTheme = {
    container: {
      bodyBackgroundColor: 'white'
    },
    dial: {
      dialBackgroundColor: '#f0f0f0',
      dialInactiveColor: '#00000085',
      dialActiveColor: 'black'
    },
    clockFace: {
      clockFaceBackgroundColor: '#f0f0f0',
      clockHandColor: '#5d87ff',
      clockFaceTimeInactiveColor: '#686868'
    }
  };

  private onChange: any = () => {};
  private onTouched: any = () => {};

  ngOnInit() {
    // Initialize with default time if provided
    if (this.dateRangePickerConfig.startTime) {
      this.startTime = this.dateRangePickerConfig.startTime;
      this.tempStartTime = this.startTime;
    }
    if (this.dateRangePickerConfig.endTime) {
      this.endTime = this.dateRangePickerConfig.endTime;
      this.tempEndTime = this.endTime;
    }
  }

  onStartTimeChange(time: string) {
    if (this.isValidTimeFormat(time)) {
      this.tempStartTime = time;
    }
  }

  onEndTimeChange(time: string) {
    if (this.isValidTimeFormat(time)) {
      this.tempEndTime = time;
    }
  }

  onApply() {
    this.startTime = this.tempStartTime;
    this.endTime = this.tempEndTime;
    this.handleDateChange();
  }

  handleDateChange() {
    let startDateString: string | null = null;
    let endDateString: string | null = null;

    if (this.startDate) {
      const startMoment = moment(this.startDate);
      if (this.startTime) {
        const [hours, minutes] = this.startTime.split(':');
        startMoment.set({ hour: parseInt(hours), minute: parseInt(minutes), second: 0 });
      }
      startDateString = startMoment.toDate().toISOString();
      // Update the startDate with the time component
      if (this.startTime) {
        const [hours, minutes] = this.startTime.split(':');
        this.startDate = moment(this.startDate)
          .set({ hour: parseInt(hours), minute: parseInt(minutes), second: 0 })
          .toDate();
      }
    }

    if (this.endDate) {
      const endMoment = moment(this.endDate);
      if (this.endTime) {
        const [hours, minutes] = this.endTime.split(':');
        endMoment.set({ hour: parseInt(hours), minute: parseInt(minutes), second: 0 });
      }

      endDateString = endMoment.toDate().toISOString();

      if (this.endTime) {
        const [hours, minutes] = this.endTime.split(':');
        this.endDate = moment(this.endDate)
          .set({ hour: parseInt(hours), minute: parseInt(minutes), second: 0 })
          .toDate();
      }
    }

    const rangeValue = {
      start: startDateString,
      end: endDateString
    };

    this.onChange(rangeValue);
    this.dateRangePickerConfig.dateChanged?.(rangeValue);
  }

  private isValidTimeFormat(time: string): boolean {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  }

  writeValue(value: any): void {
    if (value) {
      if (value.start) {
        const startMoment = moment.utc(value.start).local();
        this.startDate = startMoment.toDate();
        this.startTime = startMoment.format('HH:mm');
        this.tempStartTime = this.startTime;
      }
      if (value.end) {
        const endMoment = moment.utc(value.end).local();
        this.endDate = endMoment.toDate();
        this.endTime = endMoment.format('HH:mm');
        this.tempEndTime = this.endTime;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
}
